import type { GridPinnedColumnFields } from '@mui/x-data-grid-pro';

export const STORAGE_KEY_PREFIX = 'nexus:items-table-pinned-columns:';
const MAX_INSTANCES = 50;

export type PinnedColumnsState = { left: string[]; right: string[] };

type TablePinningEntry = {
  left: string[];
  right: string[];
  lastAccessed: number;
};

const buildKey = (instanceKey: string): string => `${STORAGE_KEY_PREFIX}${instanceKey}`;

const isValidEntry = (value: unknown): value is TablePinningEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  if (!Array.isArray(entry.left)) return false;
  if (!entry.left.every((item) => typeof item === 'string')) return false;
  if (!Array.isArray(entry.right)) return false;
  if (!entry.right.every((item) => typeof item === 'string')) return false;
  if (typeof entry.lastAccessed !== 'number') return false;
  return true;
};

export const readPinningEntry = (instanceKey: string): PinnedColumnsState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(buildKey(instanceKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEntry(parsed)) return null;
    try {
      const touched: TablePinningEntry = { ...parsed, lastAccessed: Date.now() };
      window.localStorage.setItem(buildKey(instanceKey), JSON.stringify(touched));
    } catch {}
    return { left: parsed.left, right: parsed.right };
  } catch {
    return null;
  }
};

export const writePinningEntry = (instanceKey: string, state: GridPinnedColumnFields): void => {
  if (typeof window === 'undefined') return;
  try {
    const entry: TablePinningEntry = {
      left: state.left ?? [],
      right: state.right ?? [],
      lastAccessed: Date.now(),
    };
    window.localStorage.setItem(buildKey(instanceKey), JSON.stringify(entry));
  } catch {
    // localStorage disabled or quota exceeded — silently ignore
  }
};

export const prunePinningEntries = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const tableKeys: Array<{ key: string; lastAccessed: number }> = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      try {
        const raw = window.localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        const lastAccessed =
          parsed && typeof parsed === 'object' && 'lastAccessed' in parsed
            ? (parsed as Record<string, unknown>).lastAccessed
            : 0;
        tableKeys.push({ key, lastAccessed: typeof lastAccessed === 'number' ? lastAccessed : 0 });
      } catch {
        tableKeys.push({ key, lastAccessed: 0 });
      }
    }

    if (tableKeys.length <= MAX_INSTANCES) return;

    tableKeys.sort((a, b) => a.lastAccessed - b.lastAccessed);
    const entriesToRemove = tableKeys.slice(0, tableKeys.length - MAX_INSTANCES);
    for (const { key } of entriesToRemove) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // localStorage disabled — silently ignore
  }
};
